import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import axios from 'axios';
import { toast } from 'react-toastify'; // Import toast for notifications
import { StockItem } from './core/_models'; // Import the model
import SharedData from '../../common/SharedData'; // Import SharedData for token and language
import { ImageModal } from '../../../components/common/ImageModal'; // Import ImageModal component
import { Link } from 'react-router-dom';
import {API_URL} from '../../../tenant/apiBase';

type Props = {
    item: StockItem;
    refreshData?: () => void; // Optional function to refresh data without page reload
};

const StockReportTableRow: React.FC<Props> = ({ item, refreshData }) => {
    const { formatMessage } = useIntl();
    const { currentUser, language } = SharedData(); // Get user token and language
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showImageModal, setShowImageModal] = useState<boolean>(false);
    const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null);
    const returnMatch = item.renw_return?.match(/\d+/);
    const returnId = returnMatch ? returnMatch[0] : null;

    // Which building these units sit in. Every row is one ledger batch, so the same product can
    // appear twice — once per store, or twice in one store under different expiry dates. Without
    // these two columns those rows are indistinguishable, and "Request Return" on the wrong one
    // takes the goods out of the wrong building.
    const storeLabel = item.store_name
        ? (item.store_code ? `${item.store_name} (${item.store_code})` : item.store_name)
        : '-';

    // '1970-01-01' is the ledger's no-expiry sentinel, not a date printed on any box — show it as
    // "no expiry" rather than as a date from 1970.
    const expiryLabel = (!item.expiry_date || item.expiry_date.slice(0, 10) === '1970-01-01')
        ? '-'
        : item.expiry_date.slice(0, 10);

    // Function to handle image loading errors
    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.target as HTMLImageElement;
        target.onerror = null; // Prevent infinite loop
        target.src = '/media/svg/files/blank-image.svg'; // Default placeholder
    };

    // Function to handle image click
    const handleImageClick = () => {
        // Use big_image if available, otherwise fall back to product_image
        const imageUrl = item.big_image || item.product_image || '/media/svg/files/blank-image.svg';
        setEnlargedImageUrl(imageUrl);
        setShowImageModal(true);
    };

    // Function to close image modal
    const handleCloseImageModal = () => {
        setShowImageModal(false);
        setEnlargedImageUrl(null);
    };

    // Function to handle adding product to GRR
    const handleAddToGRR = async () => {
        if (!currentUser?.token) return;
        
        setIsLoading(true);
        try {
            await axios.post(
                `${API_URL}/api/partner-portal/return-po/add-product-to-return-po`,
                {
                    product_supplier_quantity_id: item.product_supplier_quantity_id,
                    quantity: item.quantity
                },
                {
                    headers: {
                        token: currentUser.token,
                        language: language
                    }
                }
            );
            // Show success toast notification
            toast.success(formatMessage({ id: 'STOCK.ADD_TO_GRR_SUCCESS' }) || 'Product added to GRR successfully', {
                onClose: () => {
                    // Refresh data instead of reloading the page
                    if (refreshData) {
                        refreshData();
                    }
                }
            });
        } catch (error) {
            console.error('Error adding product to GRR:', error);
            // Show error toast notification
            toast.error(formatMessage({ id: 'STOCK.ADD_TO_GRR_ERROR' }) || 'Failed to add product to GRR');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <tr>{/* Remove whitespace between tr and td elements */}
                <td className='text-start'>
                    <div className='symbol symbol-100px'> {/* Increased size from 45px to 50px */}
                        <img
                            src={item.product_image || '/media/svg/files/blank-image.svg'}
                            alt={item.name}
                            onError={handleImageError}
                            onClick={handleImageClick}
                            style={{ cursor: 'pointer' }} // Add pointer cursor to indicate it's clickable
                        />
                    </div>
                </td>
                <td className='text-start'>
                    <span>{item.name}</span>
                </td>
                <td>
                    <span>{item.model || '-'}</span>
                </td>
                <td>
                    <span>{item.manufacturer_name || formatMessage({ id: 'N/A' })}</span>
                </td>
                <td>
                    <span>{item.barcode}</span>
                </td>
                <td>
                    <span>{storeLabel}</span>
                </td>
                <td>
                    <span>{expiryLabel}</span>
                </td>
                <td>
                  {/*
                    po_id 0 means this stock did not arrive on a purchase order — an adjustment, or
                    a row that predates PO tracking. It used to be linked like any other id, and the
                    link went to /orders/purchase-order/0, which renders "Purshase Order # 0 — All PO
                    items were removed": a page that does not merely 404 but tells the supplier their
                    PO was emptied, about a PO that never existed. Show the dash the expiry column
                    already uses for "there is nothing here".
                  */}
                  {Number(item.po_id) > 0 ? (
                    <Link to={`/orders/purchase-order/${item.po_id}`} className="text-primary fw-bold text-decoration-underline">
                      {item.po_id}
                    </Link>
                  ) : (
                    <span>-</span>
                  )}
                </td>
                <td className='text-end'>
                    <span>{item.quantity}</span>
                </td>
                <td className='text-center'>
                  {item.renw_return == null ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-dark"
                            onClick={handleAddToGRR}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                formatMessage({ id: 'STOCK.ADD_TO_GRR' })
                            )}
                        </button>
                                    ) : returnId ? (
                                      <span style={{ color: '#28a745' }}>
                          Already added to return PO{' '}
                                        <Link
                                          to={`/orders/return-order/${returnId}`}
                                          className="fw-bold text-primary text-decoration-underline"
                                        >
                            #{returnId}
                          </Link>
                        </span>
                                    ) : (
                    <span style={{ color: '#28a745' }}>
                      {item.renw_return}
                    </span>
                    )}
                </td>
            </tr>
            
            <ImageModal
                show={showImageModal}
                imageUrl={enlargedImageUrl}
                onClose={handleCloseImageModal}
                altText={item.name}
            />
        </>
    );
};

export default StockReportTableRow;
